import { adminMeSchema, ApiError, type AdminCapability, type AdminMe } from "@studyward/shared";
import type { Session } from "@supabase/supabase-js";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { api, supabase } from "./api";

/**
 * Admin sign-in state (§8.1): signed out → password → second factor (enrol on first sign-in, verify after) →
 * an aal2 session. The console only opens on aal2; the API refuses every admin call without it anyway.
 */
export type AuthState = { status: "loading" } | { status: "signed_out" } | { status: "needs_mfa"; session: Session } | { status: "ready"; session: Session };

const AuthContext = createContext<{ state: AuthState; refresh: () => Promise<void> }>({ state: { status: "loading" }, refresh: async () => {} });

async function resolve(session: Session | null): Promise<AuthState> {
  if (!session) return { status: "signed_out" };
  const { data: user, error: userError } = await supabase.auth.getUser();
  if (userError || !user.user) {
    await supabase.auth.signOut();
    return { status: "signed_out" };
  }
  const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (error) return { status: "needs_mfa", session };
  return data.currentLevel === "aal2" ? { status: "ready", session } : { status: "needs_mfa", session };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });
  const queryClient = useQueryClient();
  const currentUserIdRef = useRef<string | null>(null);

  const refresh = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    currentUserIdRef.current = data.session?.user?.id ?? null;
    setState(await resolve(data.session));
  }, []);

  useEffect(() => {
    void supabase.auth
      .getSession()
      .then(async ({ data }) => {
        currentUserIdRef.current = data.session?.user?.id ?? null;
        return resolve(data.session);
      })
      .then(setState);

    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      const newUserId = session?.user?.id ?? null;
      const previousUserId = currentUserIdRef.current;

      if (event === "SIGNED_OUT") {
        currentUserIdRef.current = null;
        queryClient.clear();
        setState({ status: "signed_out" });
      } else if (event === "SIGNED_IN") {
        // Only clear cache if a different admin user has signed in
        if (newUserId && previousUserId && newUserId !== previousUserId) {
          queryClient.clear();
        }
        currentUserIdRef.current = newUserId;
        setTimeout(() => void resolve(session).then((res) => {
          setState((prev) => {
            if (prev.status === "ready" && res.status === "ready" && prev.session.access_token === res.session.access_token) {
              return prev;
            }
            return res;
          });
        }), 0);
      } else if (session) {
        currentUserIdRef.current = newUserId;
        setTimeout(() => void resolve(session).then((res) => {
          setState((prev) => {
            if (prev.status === "ready" && res.status === "ready" && prev.session.access_token === res.session.access_token) {
              return prev;
            }
            return res;
          });
        }), 0);
      } else {
        currentUserIdRef.current = null;
        setState({ status: "signed_out" });
      }
    });
    return () => data.subscription.unsubscribe();
  }, [queryClient]);

  return <AuthContext.Provider value={{ state, refresh }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

/** The signed-in admin's role and capabilities from GET /admin/me — the route guard (§8.1). */
export function useMe() {
  const { state } = useAuth();
  return useQuery({
    queryKey: ["admin", "me"],
    queryFn: () => api.get("/admin/me", adminMeSchema),
    enabled: state.status === "ready",
    retry: (count, err) => !(err instanceof ApiError && (err.code === "forbidden" || err.code === "unauthorized")) && count < 1,
    staleTime: 5 * 60_000,
  });
}

const MeContext = createContext<AdminMe | null>(null);
export const MeProvider = MeContext.Provider;

export function useAdmin(): AdminMe {
  const me = useContext(MeContext);
  if (!me) throw new Error("useAdmin outside the console");
  return me;
}

export function useCan(capability: AdminCapability): boolean {
  return useAdmin().capabilities.includes(capability);
}

export async function signOut() {
  await supabase.auth.signOut();
}
